// Utility functions for segment items

/**
 * Build a stable localId like the one used in sourcePackage slice
 */
export function computeLocalId({docId, index, offset, start, end}) {
    const s = start || {};
    const e = end || {};
    const sIdx = (s.segmentIndex != null ? s.segmentIndex : index);
    const sOff = (s.segmentOffset != null ? s.segmentOffset : (offset ?? 0));
    const eIdx = (e.segmentIndex != null ? e.segmentIndex : sIdx);
    const eOff = (e.segmentOffset != null ? e.segmentOffset : sOff);
    if (docId != null && sIdx != null && sOff != null && eIdx != null && eOff != null) {
        return [docId, sIdx, sOff, eIdx, eOff].join(':');
    }
    return null;
}

/**
 * Parse canonical localId: docId:startIdx:startOff:endIdx:endOff
 * Returns null for non-canonical formats (e.g. legacy delta:* ids).
 */
export function parseCanonicalLocalId(localId) {
    if (typeof localId !== 'string') return null;
    const parts = localId.split(':');
    if (parts.length !== 5) return null;

    const [docIdPart, startIdxPart, startOffPart, endIdxPart, endOffPart] = parts;
    const parsed = {
        docId: Number(docIdPart),
        startSegmentIndex: Number(startIdxPart),
        startSegmentOffset: Number(startOffPart),
        endSegmentIndex: Number(endIdxPart),
        endSegmentOffset: Number(endOffPart),
    };

    const hasInvalid = Object.values(parsed).some((value) => !Number.isFinite(value));
    return hasInvalid ? null : parsed;
}

/**
 * Resolve segment index for open/highlight behavior.
 * Priority: explicit index -> start.segmentIndex -> canonical localId.
 */
export function resolveSegmentIndex({index, start, localId}) {
    if (index != null && Number.isFinite(Number(index))) return Number(index);
    if (start?.segmentIndex != null && Number.isFinite(Number(start.segmentIndex))) {
        return Number(start.segmentIndex);
    }

    const parsed = parseCanonicalLocalId(localId);
    if (parsed) return parsed.startSegmentIndex;

    return null;
}

/**
 * Build a stable reference key for deduplication
 */
export function refKey(r) {
    return `${r?.docId ?? ''}:${r?.segmentId ?? ''}:${r?.offset ?? 0}`;
}

/**
 * Custom comparator for SegmentItem to avoid rerendering when the parent passes a new props object
 */
export function segmentItemPropsAreEqual(prev, next) {
    const a = prev.props || {};
    const b = next.props || {};
    const keys = [
        'docId', 'docUrl', 'docTitle', 'docSource', 'docSourceType', 'classificationLevel',
        'content', 'saveContent', 'segmentId', 'index', 'offset', 'start', 'end', 'segmentType',
        'savedSegmentIndex', 'orderIndex', 'refNumber', 'query', 'displayMode', 'isSelected', 'score',
        'reportSegmentId', 'dataItemSegmentId', 'highlightDataItemId'
    ];
    for (let k of keys) {
        if (a[k] !== b[k]) return false;
    }
    // Compare callback identities too when parent handlers are replaced.
    return a.setSegments === b.setSegments
        && a.onSelectSegment === b.onSelectSegment
        && a.onContentClick === b.onContentClick;
}

